import React, { ReactNode, useContext } from "react";
import { Menu } from "@pushpress/shared-components";
import { useCalendar } from "../CalendarProvider/CalendarProvider";
import { AttendanceStatus } from "../../../graphql/graphql-generated.types";
import { AppContext } from "../../../context/app";
import { AttendanceReportFragment } from "./Attendance-generated.hooks";

interface AttendanceMenuProps {
  row: AttendanceReportFragment;
  onClose: VoidFunction;
  onSuccess: VoidFunction;
}

type AttendanceActions =
  | "cancelCheckIn"
  | "cancelReservation"
  | "checkIn"
  | "removeFromWaitList"
  | "removeLateCancel"
  | "addToClass";

const attendanceMenuMap: { [key in AttendanceStatus]: AttendanceActions[] } = {
  CANCELLED: [],
  CHECKED_IN: ["cancelCheckIn", "cancelReservation"],
  LATE_CANCEL: ["removeLateCancel"],
  NO_SHOW: ["checkIn", "cancelReservation"],
  RESERVED: ["checkIn", "cancelReservation"],
  WAIT_LIST: ["removeFromWaitList", "addToClass"],
  CHECK_IN_FAILED: ["cancelCheckIn", "cancelReservation"],
};

const source = "Core - attendance report";

const AttendanceMenu = (props: AttendanceMenuProps) => {
  const { row, onClose, onSuccess } = props;

  const app = useContext(AppContext);

  const clientUuid = app.client.uuid || app.client.client_uuid;

  const {
    cancelCheckIn,
    cancelReservation,
    checkIn,
    removeFromWaitList,
    removeLateCancel,
  } = useCalendar();

  const handleCancelCheckin = async () => {
    const checkinUuid = row.checkin?.uuid ?? row.registration?.checkinUuid;
    await cancelCheckIn({ cancelCheckinInput: { clientUuid, checkinUuid } });
    onSuccess();
    onClose();
  };

  const handleCancelReservation = async () => {
    await cancelReservation({
      cancelReservationInput: {
        clientUuid,
        reservationId: row.registration.uuid,
        isLateCancel: undefined,
      },
    });
    onSuccess();
    onClose();
  };

  const handleCheckin = async () => {
    await checkIn({
      checkinInput: {
        reservationId: row.registration.uuid,
        overrideRules: true,
        source,
      },
    });
    onSuccess();
    onClose();
  };

  const handleRemoveFromWaitList = async () => {
    await removeFromWaitList({
      cancelReservationInput: {
        clientUuid,
        reservationId: row.registration.uuid,
        isLateCancel: undefined,
      },
    });
    onSuccess();
    onClose();
  };

  const handleLateCancel = async () => {
    await removeLateCancel({
      removeLateCancelInput: {
        clientUuid,
        reservationId: row.registration.uuid,
        isLateCancel: undefined,
      },
    });
    onSuccess();
    onClose();
  };

  const menuMap: { [key in AttendanceActions]: ReactNode } = {
    cancelCheckIn: (
      <Menu.Item
        key="cancelCheckIn"
        label="Undo check in"
        action={handleCancelCheckin}
      />
    ),
    cancelReservation: (
      <Menu.Item
        key="cancelReservation"
        label="Cancel reservation"
        action={handleCancelReservation}
      />
    ),
    checkIn: (
      <Menu.Item key="checkIn" label="Check in" action={handleCheckin} />
    ),
    addToClass: (
      <Menu.Item key="addToClass" label="Add to class" action={handleCheckin} />
    ),
    removeFromWaitList: (
      <Menu.Item
        key="removeFromWaitList"
        label="Remove from wait list"
        action={handleRemoveFromWaitList}
      />
    ),
    removeLateCancel: (
      <Menu.Item
        key="removeLateCancel"
        label="Remove late cancel"
        action={handleLateCancel}
      />
    ),
  };

  const menuItems = attendanceMenuMap[row.status];

  return <Menu>{menuItems.map((name) => menuMap[name])}</Menu>;
};

export default AttendanceMenu;
